import { ABCDCreativeSettings } from "@blisspointmedia/bpm-types/dist/pgTables/ABCDCreativeSettings";
import { ABCDScoreObject } from "../ABCD/ABCDUtils";
import { convertTimelineToDateMap } from "../EditCreativeModal/Configs/TVADEditCreativeConfig";
import { CreativeMapItem, CreativeTimeline, TimelineKey } from "../../redux/creative";
import { getBackgroundColor, getColor } from "../RadialScoreChart/RadialScoreChart";
import { HAS_SCORE_ENUM, MEDIATYPE_PRETTY_NAME_MAP, View, VIEW_TYPES } from "./CreativeListUtils";
import { Header, Img, Video } from "../../Components";
import * as Dfns from "date-fns";
import * as R from "ramda";
import {
  getCreativeAsset,
  getCreativeThumbnail,
} from "../../SingleChannel/MetricsTable/metricsTableUtils";
import {
  MdCheck,
  MdCheckCircle,
  MdEdit,
  MdOutlineArrowForwardIos,
  MdOutlineHighlightOff,
  MdOutlineWarningAmber,
} from "react-icons/md";

interface CreativeNameMapItem {
  avails: string[];
  concepts: string[];
  creativeIDs: string[];
  creativeListMediatype: "Linear/Streaming";
  creativeMapByID: Record<string, CreativeMapItem>;
  creativeTags: Record<string, string[]>;
  files: string[];
  languages: string[];
  lengths: string[];
  mediatypes: string[];
  name: string;
}

interface GetToggleOptionsParams {
  selectedToggles: Record<string, Record<string, boolean>>;
  selectedView: View;
  setSelectedToggles: (selectedToggles: Record<string, Record<string, boolean>>) => void;
}

const makeLabels = (labels: string[]) =>
  R.map(label => <div className="label">{label}</div>, labels);

export const getTVADFilters = (args: GetToggleOptionsParams): JSX.Element => {
  const { selectedToggles = {}, selectedView, setSelectedToggles } = args;
  const toggleOptions = {
    mediatype: ["Streaming", "Linear"],
    length: ["15", "30", "60"],
    avail: ["National", "Local"],
    status: ["Live", "Starting", "Retired"],
    ...(selectedView !== VIEW_TYPES.CREATIVE_VIEW
      ? { [HAS_SCORE_ENUM.HAS_SCORE]: [HAS_SCORE_ENUM.HAS_SCORE, HAS_SCORE_ENUM.NO_SCORE] }
      : {}),
  };
  return (
    <>
      {R.map(
        toggleType => (
          <div className={`toggle ${toggleType}`}>
            {R.map(
              option => (
                <div
                  className={`toggleOption ${
                    selectedToggles[toggleType] && selectedToggles[toggleType][option]
                      ? "selected"
                      : ""
                  }`}
                  onClick={() =>
                    setSelectedToggles({
                      ...selectedToggles,
                      [toggleType]: {
                        ...R.defaultTo({}, selectedToggles[toggleType]),
                        [option]:
                          selectedToggles[toggleType] && selectedToggles[toggleType][option]
                            ? !selectedToggles[toggleType][option]
                            : true,
                      },
                    })
                  }
                >
                  {option}
                </div>
              ),
              toggleOptions[toggleType]
            )}
          </div>
        ),
        R.keys(toggleOptions) as string[]
      )}
    </>
  );
};

export const applyTVADFilters = (
  creativeMap: Record<string, object>,
  selectedToggles: Record<string, Record<string, boolean>>
): Record<string, object> => {
  const filteredCreativeMap = {};
  const getFilter = filterType =>
    R.filter(
      filterOption => selectedToggles[filterType][filterOption],
      R.keys(selectedToggles[filterType])
    );
  const mediatypeFilters = getFilter("mediatype");
  const lengthFilters = getFilter("length");
  const availFilters = getFilter("avail");
  const statusFilters = getFilter("status");
  for (const creativeID of R.keys(creativeMap)) {
    const creative = creativeMap[creativeID] as CreativeMapItem;
    let addCreative = true;
    if (!R.isEmpty(mediatypeFilters)) {
      for (const mediatype of mediatypeFilters) {
        if (
          creative.media_types &&
          !(creative.media_types as string[]).includes(R.toLower(mediatype))
        ) {
          addCreative = false;
          break;
        }
      }
    }
    if (
      !R.isEmpty(lengthFilters) &&
      creative.length &&
      !lengthFilters.includes(`${creative.length}`)
    ) {
      addCreative = false;
    }
    if (
      !R.isEmpty(availFilters) &&
      creative.avail &&
      !availFilters.includes(creative.avail === "L" ? "Local" : "National")
    ) {
      addCreative = false;
    }
    if (!R.isEmpty(statusFilters)) {
      if (!creative.retired && statusFilters.includes("Retired")) {
        addCreative = false;
      }
      let hasFutureDates = false;
      let hasLiveDates = false;
      for (const mediatype of R.keys(creative.timeline)) {
        if (creative.timeline) {
          const timeline = creative.timeline[mediatype];
          if (R.keys(timeline.future).length) {
            hasFutureDates = true;
          }
          if (R.keys(timeline.current).length) {
            hasLiveDates = true;
          }
          if (hasFutureDates && hasLiveDates) {
            break;
          }
        }
      }
      if (!hasFutureDates && statusFilters.includes("Starting")) {
        addCreative = false;
      }
      if (!hasLiveDates && statusFilters.includes("Live")) {
        addCreative = false;
      }
    }
    if (addCreative) {
      filteredCreativeMap[creativeID] = creative;
    }
  }
  return filteredCreativeMap;
};

interface GetTVADDescriptionContainerParams {
  creativeKey: string;
  creativeMap: Record<string, object>;
  isInternal: boolean;
  selectedView: View;
  setSelectedEditCreative: (selectedEditCreative: CreativeMapItem) => void;
}

export const getTVADDescriptionContainer = ({
  creativeKey,
  creativeMap,
  isInternal,
  selectedView,
  setSelectedEditCreative,
}: GetTVADDescriptionContainerParams): JSX.Element => {
  const creative = creativeMap[creativeKey] as CreativeNameMapItem;
  const dateMap =
    creative &&
    creative.creativeMapByID &&
    creative.creativeMapByID[creativeKey] &&
    creative.creativeMapByID[creativeKey].timeline
      ? convertTimelineToDateMap(
          creative.creativeMapByID[creativeKey].timeline as Record<TimelineKey, CreativeTimeline>
        )
      : {};
  const avails = R.flatten([R.defaultTo([], creative.avails)]);
  const concepts = R.flatten([R.defaultTo([], creative.concepts)]);
  const creativeIDs = R.flatten([R.defaultTo([], creative.creativeIDs)]);
  const languages = R.flatten([R.defaultTo([], creative.languages)]);
  const lengths = R.flatten([R.defaultTo([], creative.lengths)]);
  const mediatypes = R.flatten([R.defaultTo([], creative.mediatypes)]);
  const dates = (
    <div className="dates">
      {R.map(
        mediatype => (
          <>
            {R.map(
              date => (
                <div className="date">
                  <div className="mediatype">
                    {(mediatype as string).slice(0, 1).toUpperCase()}
                    {(mediatype as string).slice(1)}
                  </div>
                  <div className="range">
                    <div className="start">{Dfns.format(new Date(date.start), "MM/dd/yyyy")} –</div>{" "}
                    <div className={`end ${date.end ? "" : "noEndDate"}`}>
                      {date.end ? Dfns.format(new Date(date.end), "MM/dd/yyyy") : "No End Date"}
                    </div>
                  </div>
                </div>
              ),
              dateMap[mediatype] as { start: string; end: string }[]
            )}
          </>
        ),
        R.keys(dateMap)
      )}
    </div>
  );
  const mediatypesContainer = (
    <div className="mediatypes">
      {R.map(
        mediatype => (
          <div className="mediatype">{MEDIATYPE_PRETTY_NAME_MAP[mediatype]}</div>
        ),
        mediatypes
      )}
    </div>
  );
  const metadataLabels = (
    <div className="metadataLabels">
      <div className="labels">{makeLabels(R.map(length => `${length}s`, lengths))}</div>
      <div className="labels">{makeLabels(languages)}</div>
      <div className="labels">{makeLabels(avails)}</div>
    </div>
  );
  const creativeNameMap: Record<string, string[]> = {};
  for (const creativeID of R.keys(creative.creativeMapByID)) {
    const current = creative.creativeMapByID[creativeID];
    creativeNameMap[current.name] = [...R.defaultTo([], creativeNameMap[current.name]), creativeID];
  }
  const subtitleContainer = (
    <div className="subtitleContainer">
      {selectedView === VIEW_TYPES.CREATIVE_ID_VIEW ? (
        <div className="subtitle">{creative.name}</div>
      ) : selectedView === VIEW_TYPES.CREATIVE_VIEW ? (
        <div className="subtitle">{creativeIDs.join(", ")}</div>
      ) : (
        R.map(
          name => (
            <div className="creativeIDsByName">
              <div className="subtitle">{name}</div>
              <div className="label">{creativeNameMap[name].join(", ")}</div>
            </div>
          ),
          R.keys(creativeNameMap)
        )
      )}
    </div>
  );
  return (
    <div className={"descriptionContainer"}>
      <div className="titleContainer">
        <div className="title">{creativeKey}</div>
        {subtitleContainer}
        {selectedView !== VIEW_TYPES.CREATIVE_VIEW && (
          <div className="concept">Concept: {concepts.join(",")}</div>
        )}
      </div>
      {(isInternal || selectedView !== VIEW_TYPES.CREATIVE_VIEW) && (
        <div className="metadataContainer">
          {metadataLabels}
          {selectedView === VIEW_TYPES.CREATIVE_ID_VIEW ? dates : mediatypesContainer}
          {selectedView === VIEW_TYPES.CREATIVE_ID_VIEW && (
            <div
              className="editCreativeIcon"
              onClick={() => setSelectedEditCreative(creative.creativeMapByID[creativeKey])}
            >
              <MdEdit />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

interface GetTVADCreativeListByNameOrFileParams {
  abcdCreativeSettingsMap: Record<string, ABCDCreativeSettings>;
  creativeMap: Record<string, object>;
  selectedView: View;
}

export const getGroupedTVADCreativeList = ({
  abcdCreativeSettingsMap,
  creativeMap,
  selectedView,
}: GetTVADCreativeListByNameOrFileParams): Record<string, CreativeNameMapItem> => {
  const creativeNameMap: Record<string, CreativeNameMapItem> = {};
  for (const creativeID of R.keys(creativeMap)) {
    const creative = creativeMap[creativeID] as CreativeMapItem;
    const creativeIDForFile =
      selectedView === VIEW_TYPES.UNIQUE_VIDEO_VIEW || creative.avail === "L"
        ? creativeID.replace(new RegExp("LH$"), "NH")
        : creativeID;
    const filename = abcdCreativeSettingsMap[creativeIDForFile]
      ? abcdCreativeSettingsMap[creativeIDForFile].filename
      : abcdCreativeSettingsMap[creativeID]
      ? abcdCreativeSettingsMap[creativeID].filename
      : creative.file;
    const creativeKey =
      selectedView === VIEW_TYPES.CREATIVE_VIEW
        ? creative.name
        : selectedView === VIEW_TYPES.UNIQUE_VIDEO_VIEW
        ? filename
        : creativeID;
    if (creativeNameMap[creativeKey]) {
      const newCreativeTags = R.defaultTo({}, creativeNameMap[creativeKey].creativeTags);
      for (const key of R.keys(creative.creativeTags)) {
        newCreativeTags[key] = R.uniq([
          ...R.defaultTo([], newCreativeTags[key]),
          ...R.defaultTo([], creative.creativeTags[key]),
        ]);
      }
      creativeNameMap[creativeKey] = {
        ...creativeNameMap[creativeKey],
        avails: R.uniq([
          ...creativeNameMap[creativeKey].avails,
          creative.avail === "N" ? "National" : "Local",
        ]),
        concepts: R.uniq([...creativeNameMap[creativeKey].concepts, creative.concept]),
        creativeIDs: R.uniq([...creativeNameMap[creativeKey].creativeIDs, creativeID]),
        creativeListMediatype: "Linear/Streaming",
        creativeTags: newCreativeTags,
        files: R.uniq([...creativeNameMap[creativeKey].files, filename]),
        languages: R.uniq([...creativeNameMap[creativeKey].languages, creative.language]),
        lengths: R.uniq([...creativeNameMap[creativeKey].lengths, `${creative.length}`]),
        mediatypes: R.uniq([
          ...creativeNameMap[creativeKey].mediatypes,
          ...(creative.media_types as string[]),
        ]),
        creativeMapByID: {
          ...creativeNameMap[creativeKey].creativeMapByID,
          [creativeID]: creative,
        },
      };
    } else {
      creativeNameMap[creativeKey] = {
        avails: [creative.avail === "N" ? "National" : "Local"],
        concepts: [creative.concept],
        creativeIDs: [creativeID],
        creativeListMediatype: "Linear/Streaming",
        creativeTags: { ...creative.creativeTags },
        files: [filename],
        languages: [creative.language],
        lengths: [`${creative.length}`],
        mediatypes: creative.media_types,
        name: creative.name,
        creativeMapByID: {
          [creativeID]: { ...creative, file: filename },
        },
      };
    }
  }
  return creativeNameMap;
};

interface GetTVADThumbnailParams {
  company: string;
  creativeKey: string;
  creativeMap: Record<string, object>;
}

export const getTVADThumbnail = ({
  company,
  creativeKey,
  creativeMap,
}: GetTVADThumbnailParams): JSX.Element => {
  const creative = creativeMap[creativeKey];
  const creativeIDs = R.flatten(
    creative &&
      (creative as CreativeNameMapItem).creativeIDs &&
      !R.isEmpty((creative as CreativeNameMapItem).creativeIDs)
      ? (creative as CreativeNameMapItem).creativeIDs
      : [creativeKey]
  );
  return (
    <Img
      backupSrcs={R.map(creativeID => getCreativeThumbnail(company, creativeID), creativeIDs)}
      src={getCreativeThumbnail(company, creativeIDs[0])}
      unloader={<div className="creativeImageUnloader">No Thumbnail</div>}
    />
  );
};

interface GetExpandedTVADDetailsContainerParams {
  abcdCreativeSettingsMap: Record<string, ABCDCreativeSettings>;
  abcdScoreMap: Record<string, ABCDScoreObject>;
  company: string;
  creativeKey: string;
  creativeMap: Record<string, object>;
  openABCDSection: string;
  selectedView: View;
  setOpenABCDSection: (openABCDSection: string) => void;
}

export const getExpandedTVADDetailsContainer = ({
  abcdCreativeSettingsMap,
  abcdScoreMap,
  company,
  creativeKey,
  creativeMap,
  openABCDSection,
  selectedView,
  setOpenABCDSection,
}: GetExpandedTVADDetailsContainerParams): JSX.Element => {
  const creative = creativeMap[creativeKey] as CreativeNameMapItem;
  const { creativeIDs, creativeMapByID, files } = creative;
  const dateMap =
    creative &&
    creative.creativeMapByID &&
    creative.creativeMapByID[creativeKey] &&
    creative.creativeMapByID[creativeKey].timeline
      ? convertTimelineToDateMap(
          creative.creativeMapByID[creativeKey].timeline as Record<TimelineKey, CreativeTimeline>
        )
      : {};
  const avails = R.flatten([R.defaultTo([], creative.avails)]);
  const concepts = R.flatten([R.defaultTo([], creative.concepts)]);
  const languages = R.flatten([R.defaultTo([], creative.languages)]);
  const lengths = R.flatten([R.defaultTo([], creative.lengths)]);
  const mediatypes = R.flatten([R.defaultTo([], creative.mediatypes)]);
  const poster =
    creativeIDs && !R.isEmpty(creativeIDs) ? getCreativeThumbnail(company, creativeIDs[0]) : "";
  const abcdCreativeSettings = abcdCreativeSettingsMap[creativeIDs[0]];
  const file =
    abcdCreativeSettings && abcdCreativeSettings.filename
      ? abcdCreativeSettings.filename
      : files && !R.isEmpty(files)
      ? files[0]
      : "";
  const src = file ? getCreativeAsset(company, file) : "";
  const creativeNameMap: Record<string, string[]> = {};
  const mediatypeStatus = {};
  for (const creativeID of R.keys(creative.creativeMapByID)) {
    const current = creative.creativeMapByID[creativeID];
    creativeNameMap[current.name] = [...R.defaultTo([], creativeNameMap[current.name]), creativeID];
    for (const mediatype of R.keys(current.timeline)) {
      if (current.timeline) {
        const timeline = current.timeline[mediatype];
        if (["Live", "Retired"].includes(mediatypeStatus[mediatype])) {
          continue;
        }
        if (R.isNil(mediatypeStatus[mediatype])) {
          mediatypeStatus[mediatype] = "Not live";
        }
        if (current.retired) {
          mediatypeStatus[mediatype] = "Retired";
        }
        if (R.keys(timeline.future).length) {
          mediatypeStatus[mediatype] = "Starting";
        }
        if (R.keys(timeline.current).length) {
          mediatypeStatus[mediatype] = "Live";
        }
      }
    }
  }
  const subtitleContainer = (
    <div className="subtitleContainer">
      {selectedView === VIEW_TYPES.CREATIVE_ID_VIEW ? (
        <>
          <div className="subtitle">{creative.name}</div>
          {files && <div className="label">Video File: {file}</div>}
        </>
      ) : selectedView === VIEW_TYPES.CREATIVE_VIEW ? (
        <div className="subtitle larger">{creativeIDs.join(", ")}</div>
      ) : (
        R.map(
          name => (
            <div className="creativeIDsByName">
              <div className="subtitle">{name}</div>
              <div className="label">{creativeNameMap[name].join(", ")}</div>
            </div>
          ),
          R.keys(creativeNameMap)
        )
      )}
    </div>
  );
  const detailsContainer = (
    <div className="detailsContainer">
      <div className="title">Details</div>
      <div className="details">
        <div className="detailTags">
          {R.map(
            length => (
              <div className="tag">{length}s</div>
            ),
            lengths
          )}
          {R.map(
            language => (
              <div className="tag">{language}</div>
            ),
            languages
          )}
          {R.map(
            avail => (
              <div className="tag">{avail}</div>
            ),
            avails
          )}
        </div>
        {selectedView !== VIEW_TYPES.CREATIVE_VIEW && (
          <div className="detail">Concept: {concepts.join(",")}</div>
        )}
        {selectedView !== VIEW_TYPES.CREATIVE_VIEW &&
          creative &&
          creative.creativeTags &&
          creative.creativeTags.insights &&
          creative.creativeTags.insights.length && (
            <div className="detail">
              Insights Categories: {creative.creativeTags.insights.join(", ")}
            </div>
          )}
        {selectedView !== VIEW_TYPES.CREATIVE_VIEW && (
          <div className="detail">
            Click-through URL: {creativeMapByID[creativeIDs[0]].clickthroughUrl}
          </div>
        )}
      </div>
    </div>
  );
  const statusContainer = (
    <div className="statusContainer">
      <div className="title">{selectedView === VIEW_TYPES.CREATIVE_VIEW ? "Status" : "Dates"}</div>
      {selectedView !== VIEW_TYPES.CREATIVE_ID_VIEW ? (
        <div className="mediatypes">
          {R.map(
            mediatype => (
              <div className="mediatypeStatus">
                <div className="mediatype">{MEDIATYPE_PRETTY_NAME_MAP[mediatype]}</div>
                <div className="status">{mediatypeStatus[mediatype]}</div>
              </div>
            ),
            mediatypes
          )}
        </div>
      ) : (
        <div className="dates">
          {R.map(
            mediatype => (
              <>
                {R.map(
                  date => (
                    <div className="date">
                      <div className="mediatype">
                        {(mediatype as string).slice(0, 1).toUpperCase()}
                        {(mediatype as string).slice(1)}
                      </div>
                      <div className="range">
                        {Dfns.format(new Date(date.start), "MM/dd/yyyy")} –{" "}
                        {date.end ? Dfns.format(new Date(date.end), "MM/dd/yyyy") : "No End Date"}
                      </div>
                    </div>
                  ),
                  dateMap[mediatype] as { start: string; end: string }[]
                )}
              </>
            ),
            R.keys(dateMap)
          )}
        </div>
      )}
    </div>
  );
  const scoreContainer =
    selectedView !== VIEW_TYPES.CREATIVE_VIEW &&
    abcdScoreMap &&
    abcdScoreMap[creativeIDs[0]] &&
    getDetailedTVADABCDScoreContainer({
      abcdScoreMap,
      creative,
      openABCDSection,
      setOpenABCDSection,
    });
  const tagsContainer = selectedView !== VIEW_TYPES.CREATIVE_VIEW && (
    <div className="tagsContainer">
      <div className="title">Tags</div>
      <div className="tags">
        {R.map(category => {
          if (creative.creativeTags[category].length === 0) {
            return <></>;
          }
          return (
            <div className="tag">
              <div className="category">{category}</div>
              <div className="values">#{creative.creativeTags[category].join(", #")}</div>
            </div>
          );
        }, R.keys(creative.creativeTags))}
      </div>
    </div>
  );
  const border = (
    <div className="borderContainer">
      <div className="border" />
    </div>
  );
  return (
    <div className="selectedCreativeDetails">
      <div className="videoContainer">
        <Video
          backupSrcs={creative.files as string[]}
          className="videoPlayer"
          controls
          poster={poster}
          spinnerSize={100}
          src={src}
          unloader={<h3>No Video File</h3>}
        />
      </div>
      <div className="selectedCreativeDetailsContent">
        <div className="titleContainer">
          <div className="title">{creativeKey}</div>
          {subtitleContainer}
        </div>
        {border}
        {detailsContainer}
        {border}
        {statusContainer}
        {Boolean(scoreContainer) && border}
        {scoreContainer}
        {Boolean(tagsContainer) && border}
        {tagsContainer}
      </div>
    </div>
  );
};

interface GetDetailedTVADABCDScoreContainer {
  abcdScoreMap: Record<string, ABCDScoreObject>;
  creative: CreativeNameMapItem;
  openABCDSection: string;
  setOpenABCDSection: (openABCDSection: string) => void;
}

const getDetailedTVADABCDScoreContainer = ({
  abcdScoreMap,
  creative,
  openABCDSection,
  setOpenABCDSection,
}: GetDetailedTVADABCDScoreContainer): JSX.Element => {
  const scoreObject = abcdScoreMap[creative.creativeIDs[0]];
  let totalPresentFeatures = 0;
  let totalFeatures = 0;
  const sectionScoreMap: Record<
    string,
    {
      presentFeatures: number;
      sectionFeatures: number;
      scoreMap: Record<string, number>;
    }
  > = {};
  if (scoreObject) {
    for (const feature of scoreObject.annotations_evaluation.evaluated_features) {
      const { category, detected, name } = feature;
      const featurePresent = detected ? 1 : 0;
      sectionScoreMap[category] = {
        presentFeatures: sectionScoreMap[category]
          ? sectionScoreMap[category].presentFeatures + featurePresent
          : featurePresent,
        sectionFeatures: sectionScoreMap[category]
          ? sectionScoreMap[category].sectionFeatures + 1
          : 1,
        scoreMap: {
          ...(sectionScoreMap[category] && sectionScoreMap[category].scoreMap
            ? sectionScoreMap[category].scoreMap
            : {}),
          [name]: featurePresent,
        },
      };
      totalPresentFeatures += featurePresent;
      totalFeatures += 1;
    }
  }
  return (
    <div className="scoreContainer">
      <div className="title">Score</div>
      <div className="score">
        <div
          className="totalScoreContainer"
          style={{
            backgroundColor: getBackgroundColor((100.0 * totalPresentFeatures) / totalFeatures),
            color: getColor((100.0 * totalPresentFeatures) / totalFeatures),
          }}
        >
          <div className="totalScore">
            {Math.floor((totalPresentFeatures / totalFeatures) * 100)}%
          </div>
          <div className="scoreMath">
            {totalPresentFeatures}/{totalFeatures}
          </div>
        </div>
        {R.map(section => {
          const isPreforming =
            sectionScoreMap[section].presentFeatures === sectionScoreMap[section].sectionFeatures;
          return (
            <>
              <div className="sectionScoreContainer">
                <div
                  className="sectionHeaderContainer"
                  onClick={() => setOpenABCDSection(openABCDSection === section ? "" : section)}
                >
                  <div className="sectionHeader">
                    <div className={`openIcon ${openABCDSection === section ? "open" : ""}`}>
                      <MdOutlineArrowForwardIos />
                    </div>
                    <div className="label">{section}</div>
                    <div className={`scoreIcon ${isPreforming ? "" : "red"}`}>
                      {isPreforming ? <MdCheck /> : <MdOutlineWarningAmber />}
                    </div>
                  </div>
                  <div className="scoreContainer">
                    <div>
                      {sectionScoreMap[section].presentFeatures}/
                      {sectionScoreMap[section].sectionFeatures}
                    </div>
                  </div>
                </div>
                {openABCDSection === section && (
                  <div className="scoreBreakdown">
                    {R.map(
                      feature => (
                        <div className="feature">
                          <div
                            className={`icon ${
                              sectionScoreMap[section].scoreMap[feature] ? "present" : ""
                            }`}
                          >
                            {sectionScoreMap[section].scoreMap[feature] ? (
                              <MdCheckCircle />
                            ) : (
                              <MdOutlineHighlightOff />
                            )}
                          </div>
                          <div
                            className={`label ${
                              sectionScoreMap[section].scoreMap[feature] ? "present" : ""
                            }`}
                          >
                            {feature}
                          </div>
                        </div>
                      ),
                      R.keys(sectionScoreMap[section].scoreMap).sort()
                    )}
                  </div>
                )}
              </div>
              <div className="border" />
            </>
          );
        }, R.keys(sectionScoreMap).sort())}
      </div>
    </div>
  );
};

interface GetTVADTableViewHeadersParams {
  creativeOptions: { categoryData: { name: string }[] };
  selectedView: View;
}

export const getTVADTableViewHeaders = ({
  creativeOptions,
  selectedView,
}: GetTVADTableViewHeadersParams): Header[] => {
  const creativeOptionsHeaders: Header[] = [];
  if (creativeOptions && creativeOptions.categoryData) {
    for (const category of creativeOptions.categoryData) {
      creativeOptionsHeaders.push({
        flex: 1,
        label: category.name,
        name: category.name,
        renderer: row => {
          const tags: string[][] = [];
          if (row.creativeMapByID && !R.isEmpty(row.creativeMapByID)) {
            for (const creativeID of R.keys(row.creativeMapByID)) {
              const creative = row.creativeMapByID[creativeID];
              if (creative.creativeTags && creative.creativeTags[category.name]) {
                tags.push(creative.creativeTags[category.name] as string[]);
              }
            }
          }
          return R.uniq(R.flatten(tags)).join(", ");
        },
      });
    }
  }
  return [
    {
      flex: 1,
      label: selectedView.replace(/ View/g, ""),
      name: selectedView.replace(/ View/g, ""),
      renderer: row => {
        if (selectedView === VIEW_TYPES.CREATIVE_ID_VIEW) {
          return row.creativeIDs[0];
        } else if (selectedView === VIEW_TYPES.CREATIVE_VIEW) {
          return row.name;
        }
        return row.files[0];
      },
    },
    {
      flex: 1,
      label: "Model Entry",
      name: "Model Entry",
      renderer: row => {
        const modelEntries: Record<string, boolean> = {};
        if (row.creativeMapByID && !R.isEmpty(row.creativeMapByID)) {
          for (const creativeID of R.keys(row.creativeMapByID)) {
            modelEntries[row.creativeMapByID[creativeID].modelEntry] = true;
          }
        }
        delete modelEntries.undefined;
        return R.keys(modelEntries).join(", ");
      },
    },
    {
      flex: 1,
      label: "Creative Name",
      name: "Creative Name",
      renderer: row => {
        const names: Record<string, boolean> = {};
        if (row.creativeMapByID && !R.isEmpty(row.creativeMapByID)) {
          for (const creativeID of R.keys(row.creativeMapByID)) {
            names[row.creativeMapByID[creativeID].name] = true;
          }
        }
        delete names.undefined;
        return R.keys(names).join(", ");
      },
    },
    {
      flex: 1,
      label: "Length",
      name: "Length",
      renderer: row => R.map(length => `${length}s`, row.lengths).join(", "),
    },
    {
      flex: 1,
      label: "Concept",
      name: "Concept",
      renderer: row => {
        const concepts: Record<string, boolean> = {};
        if (row.creativeMapByID && !R.isEmpty(row.creativeMapByID)) {
          for (const creativeID of R.keys(row.creativeMapByID)) {
            concepts[row.creativeMapByID[creativeID].concept] = true;
          }
        }
        delete concepts.undefined;
        return R.keys(concepts).join(", ");
      },
    },
    ...creativeOptionsHeaders,
    {
      renderer: row => {
        const mediatypes: Record<string, boolean> = {};
        if (row.creativeMapByID && !R.isEmpty(row.creativeMapByID)) {
          for (const creativeID of R.keys(row.creativeMapByID)) {
            const creative = row.creativeMapByID[creativeID];
            const dateMap = convertTimelineToDateMap(creative.timeline);
            for (const mediatype of R.keys(dateMap)) {
              if (dateMap[mediatype].length) {
                mediatypes[`${mediatype.charAt(0).toUpperCase()}${mediatype.slice(1)}`] = true;
              }
            }
          }
        }
        return R.keys(mediatypes).join(", ");
      },
    },
  ].map(header => ({ ...header, contentGetter: header.renderer })) as Header[];
};
